import React from "react";
import { Helmet } from "react-helmet-async";
import { motion, Variants } from "framer-motion";
import {
  ShieldCheck,
  Briefcase,
  TrendingUp,
  LineChart,
  AlertTriangle,
  Users,
  Globe,
  Sparkles,
} from "lucide-react";

import img2 from "../../assets/women/rights.jpeg";
import img3 from "../../assets/women/finacial.jpg";
import img4 from "../../assets/women/vision.avif";
import img from "../../assets/women/mainimg.png";
import secondimg from "../../assets/women/img1.webp";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/women-empowerment`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;

const pageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${pageUrl}#webpage`,
  name: "Women Empowerment | SEVAA Foundation",
  alternateName: "She Can Fly - Women Empowerment and Financial Independence",
  url: pageUrl,
  inLanguage: "en-IN",
  description:
    "SEVAA Foundation supports women empowerment through vocational training, legal rights awareness, leadership support, financial inclusion, microfinance guidance and self-reliance programs for rural, tribal and disadvantaged women.",
  isPartOf: {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: "SEVAA Foundation",
    url: siteUrl,
  },
  publisher: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: {
      "@type": "ImageObject",
      url: logoImage,
    },
    image: defaultImage,
    slogan: "सेवा परमो धर्मः",
    areaServed: [
      { "@type": "Country", name: "India" },
      { "@type": "Place", name: "Jharkhand" },
      { "@type": "Place", name: "Chatra" },
      { "@type": "Place", name: "Pakur" },
    ],
  },
  about: [
    "Women Empowerment",
    "Vocational Training",
    "Women Skill Development",
    "Women Rights Awareness",
    "Women Financial Inclusion",
    "Women Leadership",
    "Microfinance Support",
    "Women Entrepreneurship",
    "Rural Women Empowerment",
    "Tribal Women Empowerment",
    "Social Welfare"
  ],
  primaryImageOfPage: {
    "@type": "ImageObject",
    url: defaultImage,
    width: 1200,
    height: 630,
  },
};

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${pageUrl}#service`,
  name: "Women Empowerment and Skill Development Program",
  serviceType: "Women Empowerment, Vocational Training and Financial Inclusion",
  url: pageUrl,
  provider: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
  },
  areaServed: {
    "@type": "Country",
    name: "India",
  },
  description:
    "SEVAA Foundation empowers women through skill training, legal rights awareness, leadership development, financial inclusion, mentorship and support networks for self-reliance.",
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${pageUrl}#breadcrumb`,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Women Empowerment",
      item: pageUrl,
    },
  ],
};

const WomenEmpowerment: React.FC = () => {
  const staggerContainerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const gridItemVariants: Variants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <>
      <Helmet>
        <title>Women Empowerment NGO | SEVAA Foundation</title>

        <meta
          name="title"
          content="Women Empowerment NGO | SEVAA Foundation"
        />

        <meta
          name="description"
          content="Support SEVAA Foundation's women empowerment mission for skill training, rights awareness, leadership support, financial inclusion and self-reliance programs."
        />

        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Women Empowerment NGO | SEVAA Foundation" />
        <meta property="og:description" content="Support women with skill training, legal rights awareness, financial independence, leadership and self-reliance programs." />
        <meta property="og:image" content={defaultImage} />
        <meta property="og:image:alt" content="SEVAA Foundation women empowerment and skill development program" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Women Empowerment NGO | Skill Training & Rights" />
        <meta name="twitter:description" content="Support SEVAA Foundation's women empowerment mission through skill training, rights awareness and financial inclusion." />
        <meta name="twitter:image" content={defaultImage} />
        <meta name="twitter:image:alt" content="SEVAA Foundation women empowerment and skill development program" />

        <script type="application/ld+json">
          {JSON.stringify(pageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <main className="bg-slate-50 min-h-screen font-poppins pb-20">
        {/* 1. HERO SECTION */}
        <section
          aria-labelledby="women-empowerment-heading"
          className="relative w-full h-[400px] md:h-[500px]"
        >
          <div className="absolute inset-0">
            <img
              src={img}
              alt="SEVAA Foundation women empowerment skill training and financial independence mission"
              className="w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
            />

            <div className="absolute inset-0 bg-gradient-to-r from-teal-900/90 to-black/60"></div>
          </div>

          <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-8 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl"
            >
              <span className="inline-block py-1 px-3 rounded-full bg-orange-500/20 text-orange-400 font-bold text-xs uppercase tracking-widest mb-4 border border-orange-500/30">
                She Can Fly
              </span>

              <h1
                id="women-empowerment-heading"
                className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6"
              >
                Women&apos;s Empowerment <br />
                <span className="text-orange-400">
                  Equality & Independence
                </span>
              </h1>

              <p className="text-teal-50 text-base md:text-lg font-medium leading-relaxed mb-8 max-w-2xl opacity-90">
                Empowering women with skills, rights, leadership and financial
                independence to build a better tomorrow for families and entire
                communities.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 2. THE HARSH REALITY */}
        <section
          aria-labelledby="women-reality-heading"
          className="max-w-7xl mx-auto px-6 lg:px-8 -mt-20 relative z-10 mb-20"
        >
          <h2 id="women-reality-heading" className="sr-only">
            Women Empowerment Reality in India
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -12 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 border-b-[6px] border-b-rose-500 group transition-all duration-300"
            >
              <LineChart className="w-12 h-12 text-rose-500 mb-6 group-hover:scale-110 transition-transform" />

              <h3 className="text-2xl font-black text-slate-900 mb-2">
                Economic Gap
              </h3>

              <p className="text-slate-600 font-medium text-sm leading-relaxed">
                Female labour force participation remains below that of men in
                many rural and tribal regions, limiting income, independence and
                household decision-making power.
              </p>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -12 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 border-b-[6px] border-b-amber-500 group transition-all duration-300"
            >
              <AlertTriangle className="w-12 h-12 text-amber-500 mb-6 group-hover:scale-110 transition-transform" />

              <h3 className="text-2xl font-black text-slate-900 mb-2">
                Systemic Barriers
              </h3>

              <p className="text-slate-600 font-medium text-sm leading-relaxed">
                Disadvantaged women often face barriers in education,
                decision-making, legal rights awareness, healthcare access and
                financial control.
              </p>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -12 }}
              viewport={{ once: true }}
              className="bg-teal-800 p-8 rounded-3xl shadow-xl border border-teal-700 text-white border-b-[6px] border-b-teal-400 group transition-all duration-300"
            >
              <Users className="w-12 h-12 text-teal-300 mb-6 group-hover:scale-110 transition-transform" />

              <h3 className="text-2xl font-black mb-2">Community Impact</h3>

              <p className="text-teal-100 font-medium text-sm leading-relaxed">
                Empowering a woman directly improves her family&apos;s
                wellbeing, children&apos;s education, health, income stability
                and community progress.
              </p>
            </motion.article>
          </div>
        </section>

        {/* 3. THE GROUND REALITY */}
        <section
          aria-labelledby="women-ground-heading"
          className="max-w-7xl mx-auto px-6 lg:px-8 py-16"
        >
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3">
                <span className="w-12 h-[2px] bg-teal-600"></span>

                <span className="text-teal-700 font-bold text-xs uppercase tracking-widest">
                  Beyond Education
                </span>
              </div>

              <h2
                id="women-ground-heading"
                className="text-3xl md:text-4xl font-black text-slate-900 leading-[1.2]"
              >
                Why systematic change is the need of the hour?
              </h2>

              <p className="text-slate-600 leading-relaxed font-medium">
                While women&apos;s literacy has improved, true economic
                participation, financial independence and leadership
                opportunities are still out of reach for many women in rural and
                disadvantaged communities.
              </p>

              <p className="text-slate-600 leading-relaxed font-medium">
                Systematic change requires ongoing grassroots efforts so women
                can make informed decisions for themselves, their households and
                their communities.
              </p>

              <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-2xl mt-8">
                <p className="text-slate-800 font-bold italic">
                  “SEVAA Foundation focuses on vocational training, leadership,
                  rights awareness and support networks so women can thrive
                  socially and economically.”
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative h-[500px] rounded-3xl overflow-hidden shadow-2xl"
            >
              <img
                src={secondimg}
                alt="SEVAA Foundation women empowerment ground work and skill development"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>
        </section>

        {/* 4. SEVAA'S INTERVENTION */}
        <section
          aria-labelledby="women-strategy-heading"
          className="bg-white py-24 border-t border-slate-200 mt-10"
        >
          <div className="max-w-[90rem] mx-auto px-6 lg:px-8">
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <h2
                id="women-strategy-heading"
                className="text-3xl md:text-4xl font-black text-slate-900 mb-4"
              >
                Our Empowerment Strategy
              </h2>

              <p className="text-slate-500 font-medium text-lg">
                SEVAA Foundation provides the tools, training, mentorship and
                support networks required for true independence.
              </p>
            </div>

            <motion.div
              variants={staggerContainerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 auto-rows-fr"
            >
              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src="https://www.borgenmagazine.com/wp-content/uploads/2022/10/51413382636_7a858400cb_k.jpg"
                  alt="Vocational training for women by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-slate-50 p-8 rounded-[2rem] shadow-lg border border-slate-100 flex flex-col justify-center hover:-translate-y-2 hover:shadow-orange-500/10 hover:border-orange-200 transition-all group min-h-[350px]"
              >
                <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-orange-50 transition-colors">
                  <Briefcase className="w-7 h-7 text-orange-500" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Vocational Training
                </h3>

                <p className="text-slate-600 font-medium text-sm leading-relaxed">
                  Practical training in tailoring, computer skills, handicrafts
                  and livelihood skills to help women become self-reliant.
                </p>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src={img2}
                  alt="Women rights awareness and leadership training by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-slate-50 p-8 rounded-[2rem] shadow-lg border border-slate-100 flex flex-col justify-center hover:-translate-y-2 hover:shadow-teal-500/10 hover:border-teal-200 transition-all group min-h-[350px]"
              >
                <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-teal-50 transition-colors">
                  <ShieldCheck className="w-7 h-7 text-teal-600" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Leadership & Rights
                </h3>

                <p className="text-slate-600 font-medium text-sm leading-relaxed">
                  Workshops on legal rights, confidence-building, leadership and
                  support networks for decision-making power.
                </p>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src={img3}
                  alt="Women financial inclusion and microfinance support by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-slate-50 p-8 rounded-[2rem] shadow-lg border border-slate-100 flex flex-col justify-center hover:-translate-y-2 hover:shadow-amber-500/10 hover:border-amber-200 transition-all group min-h-[350px]"
              >
                <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-amber-50 transition-colors">
                  <TrendingUp className="w-7 h-7 text-amber-500" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Financial Inclusion
                </h3>

                <p className="text-slate-600 font-medium text-sm leading-relaxed">
                  Microfinance guidance, mentorship and small business support
                  to help women start income-generating activities.
                </p>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src={img4}
                  alt="Women health awareness and empowerment vision by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-teal-900 p-8 rounded-[2rem] shadow-lg flex flex-col justify-center hover:-translate-y-2 hover:shadow-teal-900/40 transition-all min-h-[350px] text-white overflow-hidden relative"
              >
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-teal-800 rounded-full blur-2xl opacity-50"></div>

                <div className="w-14 h-14 bg-teal-800 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <Sparkles className="w-7 h-7 text-teal-300" />
                </div>

                <h3 className="text-xl font-bold mb-3">Our Vision</h3>

                <p className="text-teal-100 font-medium text-sm leading-relaxed mb-6">
                  When you empower a woman, you empower a nation. SEVAA
                  Foundation ensures she has skills, confidence and support to
                  lead.
                </p>

                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-2.5 rounded-full transition-colors text-sm w-fit"
                  aria-label="Support SEVAA Foundation women empowerment mission"
                >
                  Support Her
                  <Globe className="w-4 h-4" />
                </a>
              </motion.article>
            </motion.div>
          </div>
        </section>

      </main>
    </>
  );
};

export default WomenEmpowerment;